import { useMemo, useRef } from "react"

export interface EngagementFormValidationState {
  core: boolean
  billing: boolean
  milestones: boolean
  revenueRecognition: boolean
  workers: boolean
  timesheet: boolean
  tasks: boolean
  invoice: boolean
  documents: boolean
}

// Engagement form validation hook that memoizes each section validation separately
export const useEngagementFormValidation = (validationState: EngagementFormValidationState) => {
  // Memoize individual section completions to prevent unnecessary recalculations
  const coreComplete = useMemo(() => validationState.core, [validationState.core])
  const billingComplete = useMemo(() => validationState.billing && validationState.milestones && validationState.revenueRecognition, [validationState.billing, validationState.milestones, validationState.revenueRecognition])
  const workersComplete = useMemo(() => validationState.workers, [validationState.workers])
  const timesheetComplete = useMemo(() => validationState.timesheet, [validationState.timesheet])
  const tasksComplete = useMemo(() => validationState.tasks, [validationState.tasks])
  const invoiceComplete = useMemo(() => validationState.invoice, [validationState.invoice])
  const documentsComplete = useMemo(() => validationState.documents, [validationState.documents])

  // Use ref to store stable object reference when values don't change
  const prevCompletedSectionsRef = useRef<Record<string, boolean>>({})
  
  const completedSections = useMemo(() => {
    const newValues = {
      core: coreComplete,
      billing: billingComplete,
      workers: workersComplete,
      timesheet: timesheetComplete,
      tasks: tasksComplete,
      invoice: invoiceComplete,
      documents: documentsComplete
    }
    
    // Early return if no previous values exist
    if (Object.keys(prevCompletedSectionsRef.current).length === 0) {
      prevCompletedSectionsRef.current = { ...newValues }
      return prevCompletedSectionsRef.current
    }
    
    // Check if any values actually changed using faster comparison
    let hasChanged = false
    for (const key in newValues) {
      if (prevCompletedSectionsRef.current[key] !== newValues[key as keyof typeof newValues]) {
        hasChanged = true
        break
      }
    }
    
    if (!hasChanged) {
      // Return the same object reference if values haven't changed
      return prevCompletedSectionsRef.current
    }
    
    // Values have changed, create new object and update ref
    prevCompletedSectionsRef.current = { ...newValues }
    return prevCompletedSectionsRef.current
  }, [coreComplete, billingComplete, workersComplete, timesheetComplete, tasksComplete, invoiceComplete, documentsComplete])

  return {
    completedSections
  }
} 